// registerSW — wires vite-plugin-pwa's virtual register helper.
//
// On a new SW version, auto-apply and reload immediately. Mid-flight a
// learner sees a brief reload (~200ms) but doesn't have to think about
// "tap the toast" — for a 2-day event we want the latest build always.
// Audio cache lives in `gb-audio`, independent of the precache, so the
// reload does NOT lose downloaded mp3s.
//
// Auto-check cadence (Day 14b; tightened to 10s for activity day):
// vite-plugin-pwa only checks for a new SW on page load by default.
// That meant a learner who left the PWA open after a deploy would
// never see the update — they'd have to manually close and reopen.
// We ping sw.js every 10s while the tab is visible, and also when it
// returns to the foreground from background. 10s (was 60s) so a
// mid-activity redeploy reaches foreground learners within seconds;
// the check is cheap (304 most of the time, ~hundreds of bytes) and
// only runs while visible, so battery/data cost is negligible.
// Note: this only helps foreground PWAs — iOS freezes JS in the
// background, so backgrounded learners still update on the next
// visibilitychange (foreground return), which is already wired below.
//
// History: 6af6a66 introduced a toast-with-button pattern that
// required the learner to tap 套用. Activity-prep (2026-05-27) reverted
// to the Day 10 auto-reload behavior because workers / learners
// shouldn't have to interpret an update prompt mid-walk.
//
// The PWA_UPDATE_EVENT export + applyPendingUpdate / hasPendingUpdate
// stubs are retained so the (now-vestigial) <PWAUpdateToast /> mount
// in App.tsx doesn't break — the toast simply never opens because no
// event is dispatched.

import { registerSW } from "virtual:pwa-register";

/** Retained for backward compat with <PWAUpdateToast />; never fires
 *  now that updates auto-apply. */
export const PWA_UPDATE_EVENT = "pwa-update-ready";

const POLL_INTERVAL_MS = 10_000;
const AUDIO_BUSY_RETRY_MS = 30_000;

/** True if any <audio> element on the page is currently playing.
 *  Used to defer SW reload so a learner mid-listen doesn't get cut off. */
function isAudioPlaying(): boolean {
  if (typeof document === "undefined") return false;
  const audios = document.querySelectorAll("audio");
  for (const el of Array.from(audios)) {
    const a = el as HTMLAudioElement;
    if (!a.paused && !a.ended && a.currentTime > 0) return true;
  }
  return false;
}

export function startSW(): void {
  if (typeof window === "undefined") return;
  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      const tick = () => {
        if (document.visibilityState !== "visible") return;
        registration.update().catch(() => {});
      };
      window.setInterval(tick, POLL_INTERVAL_MS);
      // Returning to foreground is the single highest-yield moment to catch
      // a deploy that landed while the PWA was backgrounded. A single check
      // here is fragile: iOS thaws the PWA's JS before the network stack is
      // fully back, so that first registration.update() often fails silently
      // (the .catch swallows it) and the learner is stuck on the old build
      // until they manually swipe-kill. Fire a short burst instead — now,
      // +2s, +5s — so a transient wake-up failure self-heals within seconds
      // without any UI. Each check is a cheap 304; the burst only runs on
      // the foreground transition, not continuously.
      const FOREGROUND_RECHECK_MS = [0, 2_000, 5_000];
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        for (const delay of FOREGROUND_RECHECK_MS) {
          if (delay === 0) tick();
          else window.setTimeout(tick, delay);
        }
      });
    },
    onNeedRefresh() {
      // Auto-apply + reload. Day 10 behavior restored.
      //
      // Belt-and-braces: clear the workbox precache before reloading so
      // we don't end up in the "new index.html + stale chunk filenames"
      // race we saw on iPhone 11 PWA standalone — ErrorBoundary kept
      // catching `Failed to fetch dynamically imported module` because
      // the new index referenced new chunks but the old SW served the
      // old precache. Keeping gb-audio / gb-tiles / gb-img / gb-fonts
      // intact so learners don't lose downloaded mp3 / map tiles on
      // every update.
      //
      // Activity-prep 2026-05-27:if the learner is currently listening
      // to an audio segment, defer the reload — interrupting playback
      // mid-walk reads as "the app crashed". Re-check every 30s until
      // they pause, then swap.
      const KEEP = new Set(["gb-audio", "gb-tiles", "gb-img", "gb-fonts"]);
      const swap = async () => {
        try {
          if ("caches" in self) {
            const keys = await caches.keys();
            await Promise.all(
              keys.filter((k) => !KEEP.has(k)).map((k) => caches.delete(k)),
            );
          }
        } catch {
          /* ignore — reload anyway */
        }
        await updateSW(true);
      };
      const tryReload = () => {
        if (isAudioPlaying()) {
          window.setTimeout(tryReload, AUDIO_BUSY_RETRY_MS);
          return;
        }
        void swap();
      };
      tryReload();
    },
    onOfflineReady() {
      // No-op; the <OfflineIndicator /> is enough.
    },
  });
}

/** Retained no-op for backward compat with <PWAUpdateToast />. */
export async function applyPendingUpdate(): Promise<void> {
  // intentionally empty — updates auto-apply now
}

/** Retained no-op for backward compat with <PWAUpdateToast />. */
export function hasPendingUpdate(): boolean {
  return false;
}
